import { NOTE_MAX_LENGTH, type Level, type PushMode, type SeatState } from './constants';
import { refsEqual } from './refs';
import { parseTagPath } from './item-tags';
import { holdsSeat, type Actor } from './seats';
import type { Standings } from './tags';
import { m } from '@/i18n';
import {
  errOf,
  ok,
  type Command,
  type CreateWorkItemInput,
  type ItemTagValue,
  type ProposedEvent,
  type Result,
  type WorkItemState,
} from './types';

/**
 * The contract state machine (docs/CONTRACT_SPINE.md, settled 2026-07-06). Pure:
 * given the current state, a command, and the acting principal, it returns either
 * the events to append or a domain error. It performs no I/O. The write spine
 * (src/server) calls this inside a transaction and applies the returned events to
 * the log and the projection.
 *
 * Among an item's audience, acting is open and only flagged (Prinzip 20, like a
 * git repo: no admin bit needed to do the work). Sight is private by default and
 * the one reserved act (replacing the requester seat) is gated in the server. The
 * axes (§5, as reshaped by docs/CLOSED_AXIS.md, three atoms 2026-07-22):
 *   - the lifecycle ATOMS `delivered`/`active`/`closed`, each moved by
 *     its own act with **free movement** (no ordering graph, no preconditions);
 *     an out-of-party move is flagged, never blocked (§5.1). There is no phase
 *     field - richer vocabulary is a flow's authored mapping, and an
 *     item in no flow has no phase at all (Leon's ruling 2026-07-21).
 *   - **verdict** (`open|disputed|accepted`) is the reviewer's statement against
 *     the definition, kept as the RECORD of the last review (2026-11-03): it
 *     survives a Retract (a Deny is Dispute then Retract), and the next Tender
 *     clears it in the SAME event. A contributor exercising the verdict is
 *     self-dealing: flagged `critical`, not blocked.
 *
 * Seats (§2): `requesters` are the held requester seats (seated at birth; an
 * explicit walk-away may empty them - vacancy semantics in
 * docs/HEALTH_SIGNALS.md §4), `contributors` the held contributor seats
 * (empty = open). **Claiming = seating yourself, nothing more** (§6, the
 * inference law): no phase moves, no verdict touched. Offering a seat to
 * another party (`seat_offers`, push rules) is a composing server write, not
 * part of this fold, because it touches a different projection table.
 *
 * What stays a hard error is **structural** (a no-op, an empty title, a verdict on
 * undelivered work): integrity, not authority.
 *
 * Group seats ARE resolved (2026-07-25): a person who belongs to a group
 * holding a seat holds that seat, via the `speaksFor` set the caller passes in
 * (`holdsSeat`). What is still not modelled is WHICH member decides
 * (`decision_rule`: lead / quorum / pull-from-pool) - the rule here is `any`.
 * See the board item "Known gaps (July): access and tenancy".
 */

/* Seat resolution (who holds which seat, groups expanded) lives in
 * `domain/seats.ts` - the ONE home, lint-enforced. These wrappers keep the
 * fold's call sites reading in its own vocabulary. */
function isRequester(actor: Actor, s: WorkItemState): boolean {
  return holdsSeat(actor, s.requesters);
}
function isContributor(actor: Actor, s: WorkItemState): boolean {
  return holdsSeat(actor, s.contributors);
}
function isParty(actor: Actor, s: WorkItemState): boolean {
  return isRequester(actor, s) || isContributor(actor, s);
}

/** Normalise a seat set: drop blanks and duplicates, preserving order. */
export function dedupeRefs(refs: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of refs) {
    if (r && r.length > 0 && !seen.has(r)) {
      seen.add(r);
      out.push(r);
    }
  }
  return out;
}

/** Order-insensitive equality of two ref sets. */
function sameRefSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const bs = new Set(b);
  return a.every((r) => bs.has(r));
}

function single(event: ProposedEvent): Result<ProposedEvent[]> {
  return ok([event]);
}

/**
 * Out-of-role flag (Prinzip 20). Returns `warning` when the actor is not in the
 * expected role, otherwise undefined (in-role activity is plain `info`). The
 * action is permitted regardless; the level is how the log surfaces it.
 */
function flag(
  actor: Actor,
  s: WorkItemState,
  expected: 'requester' | 'contributor' | 'party',
): Level | undefined {
  const inRole =
    expected === 'requester'
      ? isRequester(actor, s)
      : expected === 'contributor'
        ? isContributor(actor, s)
        : isParty(actor, s);
  return inRole ? undefined : 'warning';
}

/**
 * The verdict belongs to the requester. A contributor passing verdict on their
 * own delivery is self-dealing, so `critical`; any other out-of-role actor is
 * `warning`; the requester is in-role (`info`). (§5.2.)
 */
function verdictLevel(actor: Actor, s: WorkItemState): Level | undefined {
  if (isRequester(actor, s)) return undefined;
  if (isContributor(actor, s)) return 'critical';
  return 'warning';
}

/**
 * Solo seat (docs/CLOSED_AXIS.md, the cancellation level law): the actor is
 * the only party anywhere on the item - `requesters === [actor]` and
 * `contributors` is `[]` or `[actor]`. A group-held seat is never solo (a
 * group ref never equals a person actor), so cancelling work a group is
 * party to stays loud.
 *
 * Deliberately NOT taught about `speaksFor` (2026-07-25): speaking for a group
 * makes you in-ROLE, it does not make you the only party. Cancelling what a
 * group is party to is exactly the case the loud level exists for, even when
 * you are the member cancelling it.
 *
 * It sets the event LEVEL only, never authority, so a narrowed principal
 * (docs/AGENT_ACCESS.md §4) reads it unnarrowed.
 */
function isSoloSeat(actorRef: string, s: WorkItemState): boolean {
  const soloRequester = s.requesters.length === 1 && refsEqual(s.requesters[0]!, actorRef);
  const soloContributors =
    s.contributors.length === 0 ||
    (s.contributors.length === 1 && refsEqual(s.contributors[0]!, actorRef));
  return soloRequester && soloContributors;
}

/**
 * Decide the events produced by applying `command` to `state` as `actor` (an
 * encoded ref). Returns an error result for expected rejections.
 */
export function decide(
  state: WorkItemState,
  command: Command,
  actor: Actor,
): Result<ProposedEvent[]> {
  switch (command.type) {
    // --- The lifecycle ACTS + the activity declaration ----------------------
    // docs/CLOSED_AXIS.md: each act writes ONLY its own atom(s) (Shape A column
    // delta) - there is no phase to re-materialize. No seat fold, no Claim
    // coupling (the inference law, §6). The `on_hold ⇒ ¬active` coupling and
    // the cascade wiring are the write spine's, not this pure fold's.

    case 'Tender': {
      // Deliver. Legal from any UNDELIVERED standing (flag-not-block, §5.1); an
      // out-of-party actor is flagged, never blocked.
      if (state.delivered) {
        return errOf('no_change', m('itemErrors.contract.alreadyDelivered'));
      }
      // The core decoupling (docs/CLOSED_AXIS.md): delivering is the
      // contributor's CLAIM - it does not conclude. Tender writes `delivered`
      // and nothing else: no closure, no `active` fold. Concluding is `Close`,
      // its own act on its own axis.
      // A new delivery starts a new review (docs/CLOSED_AXIS.md "the verdict
      // as record", Leon 2026-11-03): the verdict is the record of the LAST
      // review and stands through the Deny that sent the work back, so the
      // next Tender clears it in the same event - the verdict-void seam moved
      // here from Retract.
      const clearsVerdict = state.verdict !== 'open';
      return single({
        action: 'tendered',
        previousValue: { delivered: false, ...(clearsVerdict ? { verdict: state.verdict } : {}) },
        newValue: { delivered: true, ...(clearsVerdict ? { verdict: 'open' } : {}) },
        level: flag(actor, state, 'party'),
      });
    }

    case 'Retract': {
      // Withdraw the delivery. Only meaningful from a delivered standing.
      if (!state.delivered) {
        return errOf('invalid_state', m('itemErrors.contract.notDelivered'));
      }
      // Clear delivered, nothing else. `active` is left as-is (orthogonality):
      // a still-active item resumes honestly active; the conclusion axis is
      // untouched (a closed item stays closed - retracting the claim does not
      // reopen the episode; that is `Reopen`). The verdict stays too since
      // 2026-11-03 (the verdict as record): a Deny is Dispute then Retract, and
      // the Denied record must survive the Retract; the next Tender clears it,
      // and Reopen resolves it explicitly.
      return single({
        action: 'tender_retracted',
        previousValue: { delivered: true },
        newValue: { delivered: false },
        level: flag(actor, state, 'party'),
      });
    }

    case 'Close': {
      // Conclude (docs/CLOSED_AXIS.md): the ONE conclusion, reversible, on its
      // own axis. Touches ONLY `closed` - not delivered (closing without a
      // deliverable is the cancellation READING, a real state), not the
      // verdict, and not `active` (the declaration persists so Reopen restores
      // it; `activeOf` reads false while closed because closed dominates
      // display). The optional reason rides the event; empty/whitespace is
      // treated as absent.
      if (state.closed) {
        return errOf('no_change', m('resolution.refusal.alreadyClosed'));
      }
      const reason = command.reason?.trim();
      // The cancellation level law: closing while UNDELIVERED is a cancel, and
      // when the actor is not a solo seat the event carries at least `warning`
      // (flag, never block - Prinzip 20; cancelling your own one-person
      // undertaking stays routine). The cancellation reading also RIDES the
      // event (`delivered: false` in the delta - a no-op on the column,
      // applied verbatim), so the feed's cancelled-crossing lane and the
      // dependency border classify "concluded without delivery" from the
      // event alone.
      const cancels = !state.delivered;
      const loudCancel = cancels && !isSoloSeat(actor.ref, state);
      return single({
        action: 'concluded',
        previousValue: { closed: false, ...(cancels ? { delivered: false } : {}) },
        newValue: { closed: true, ...(cancels ? { delivered: false } : {}) },
        comment: reason && reason.length > 0 ? reason : undefined,
        level: loudCancel ? 'warning' : flag(actor, state, 'party'),
      });
    }

    case 'Reopen': {
      // Un-conclude (docs/CLOSED_AXIS.md): clears `closed` and nothing else
      // (there is nothing left to un-retire). delivered/verdict/active stay as
      // they were - the prior `active` declaration simply reads again.
      if (!state.closed) {
        return errOf('invalid_state', m('itemErrors.contract.notClosed'));
      }
      return single({
        action: 'reopened',
        previousValue: { closed: true },
        newValue: { closed: false },
        level: flag(actor, state, 'party'),
      });
    }

    case 'SetActive': {
      // Declare in-progress, or clear the declaration (§1.3). A declaration, not an
      // act with a counterparty; kept honest by the coherence law (the machine
      // never declares activity, and the quiet/late facts fire THROUGH the flag).
      // The `on_hold ⇒ ¬active` coupling (declaring active clears a worn rest
      // tag, and vice versa) is the write spine's (§1.4); the pure decider only
      // moves the flag.
      if (command.active === state.active) {
        return errOf(
          'no_change',
          command.active
            ? m('itemErrors.contract.alreadyActive')
            : m('itemErrors.contract.alreadyIdle'),
        );
      }
      return single({
        action: 'activity_declared',
        previousValue: { active: state.active },
        newValue: { active: command.active },
        level: flag(actor, state, 'party'),
      });
    }

    case 'Claim': {
      // Seat yourself into the contributor set, nothing else moves (§6). Held
      // immediately. Nothing is terminal, so a claim on any phase is legal
      // (claiming a `done` item is the start of rework).
      if (isContributor(actor, state)) {
        return errOf('no_change', m('itemErrors.contract.alreadyContributor'));
      }
      return single({
        action: 'reassigned',
        previousValue: { contributors: state.contributors },
        // The seat records the PERSON, never the groups they speak for:
        // claiming seats yourself (docs/CONTRACT_SPINE.md §6).
        newValue: { contributors: [...state.contributors, actor.ref] },
      });
    }

    case 'Accept': {
      if (state.requesters.length === 0) {
        return errOf('invalid_state', m('itemErrors.contract.noRequesterToAccept'));
      }
      if (!(state.delivered ?? false)) {
        return errOf('invalid_state', m('itemErrors.contract.acceptNeedsDelivery'));
      }
      if (state.verdict === 'accepted') {
        return errOf('no_change', m('itemErrors.contract.alreadyAccepted'));
      }
      // The verdict stays binary; the caveat ("Accept mit Auflagen") rides the
      // event, permanently visible, symmetric to Dispute.
      const note = command.note?.trim();
      return single({
        action: 'accepted',
        previousValue: { verdict: state.verdict },
        newValue: { verdict: 'accepted' },
        comment: note && note.length > 0 ? note : undefined,
        level: verdictLevel(actor, state),
      });
    }

    case 'Dispute': {
      if (state.requesters.length === 0) {
        return errOf('invalid_state', m('itemErrors.contract.noRequesterToDispute'));
      }
      if (!(state.delivered ?? false)) {
        return errOf('invalid_state', m('itemErrors.contract.disputeNeedsDelivery'));
      }
      if (state.verdict === 'disputed') {
        return errOf('no_change', m('itemErrors.contract.alreadyDisputed'));
      }
      return single({
        action: 'disputed',
        previousValue: { verdict: state.verdict },
        newValue: { verdict: 'disputed' },
        comment: command.reason,
        level: flag(actor, state, 'requester'),
      });
    }

    case 'Resolve': {
      // Withdraw ANY standing verdict back to open (widened 2026-07-22, the
      // settable-attribute canon: `verdict: none` needs an act path from
      // `accepted` too - a re-review needs no dispute-then-resolve dance).
      // A logged, requester-graded act like the other verdict moves.
      if (state.requesters.length === 0) {
        return errOf('invalid_state', m('itemErrors.contract.noRequesterToResolve'));
      }
      if (state.verdict === 'open') {
        return errOf('invalid_state', m('itemErrors.contract.noVerdict'));
      }
      // Withdrawing an ACCEPTANCE is never self-dealing: the contributor gives
      // up a judgment in their own favour (a Reopen of their accepted work,
      // 2026-11-03), so it is out of role (`warning`), not `critical`.
      // Withdrawing a denial keeps the verdict's own grade.
      const level =
        state.verdict === 'accepted' && !isRequester(actor, state)
          ? 'warning'
          : verdictLevel(actor, state);
      return single({
        action: 'resolved',
        previousValue: { verdict: state.verdict },
        newValue: { verdict: 'open' },
        level,
      });
    }

    case 'SetPriority': {
      if (command.priority === state.priority) {
        return errOf('no_change', m('itemErrors.contract.priorityUnchanged'));
      }
      return single({
        action: 'field_changed',
        previousValue: { priority: state.priority },
        newValue: { priority: command.priority },
        level: flag(actor, state, 'requester'),
      });
    }

    case 'SetAcceptanceCriteria': {
      if (command.text === state.acceptanceCriteria) {
        return errOf('no_change', m('itemErrors.contract.criteriaUnchanged'));
      }
      return single({
        action: 'field_changed',
        previousValue: { acceptanceCriteria: state.acceptanceCriteria },
        newValue: { acceptanceCriteria: command.text },
        level: flag(actor, state, 'requester'),
      });
    }

    // The description is free-form context, not a contract term: either party may
    // edit it, on any phase, without an out-of-role flag.
    case 'SetDescription': {
      if (command.text === state.description) {
        return errOf('no_change', m('itemErrors.contract.descriptionUnchanged'));
      }
      return single({
        action: 'field_changed',
        previousValue: { description: state.description },
        newValue: { description: command.text },
      });
    }

    // The note is a short prose NOTICE ("what is going on"), shown in highlight
    // position. Like the description, either party may edit it on any phase without
    // an out-of-role flag; it is colourless context, never a health signal. Empty
    // input normalizes to null (absent); the hard cap is enforced here so the log
    // and every read see a bounded value.
    case 'SetNote': {
      const next = command.text === null || command.text.trim() === '' ? null : command.text;
      if (next !== null && next.length > NOTE_MAX_LENGTH) {
        return errOf(
          'invalid_input',
          m('itemErrors.contract.noteTooLong', {
            max: String(NOTE_MAX_LENGTH),
            length: String(next.length),
          }),
        );
      }
      if (next === state.note) {
        return errOf('no_change', m('itemErrors.contract.noteUnchanged'));
      }
      return single({
        action: 'field_changed',
        previousValue: { note: state.note },
        newValue: { note: next },
      });
    }

    // The title is the contract's shared name: like the description, either party
    // may edit it without an out-of-role flag, but it can never be empty.
    case 'SetTitle': {
      const title = command.text.trim();
      if (title === '') {
        return errOf('invalid_input', m('itemErrors.contract.titleNeeded'));
      }
      if (title === state.title) {
        return errOf('no_change', m('itemErrors.contract.titleUnchanged'));
      }
      return single({
        action: 'field_changed',
        previousValue: { title: state.title },
        newValue: { title },
      });
    }

    case 'SetVisibility': {
      if (command.visibility === state.visibility) {
        return errOf('no_change', m('itemErrors.contract.visibilityUnchanged'));
      }
      return single({
        action: 'field_changed',
        previousValue: { visibility: state.visibility },
        newValue: { visibility: command.visibility },
        level: flag(actor, state, 'party'),
      });
    }

    case 'SetReviewRequired': {
      // The requester's setting (docs/CLOSED_AXIS.md "Review required",
      // 2026-11-03): whether the work goes through review before it is done.
      // Graded like the verdict acts, flag-not-block: the requester is in
      // role; a contributor turning it OFF waives the review of their own work
      // (self-dealing, `critical`), turning it on is only out of role; anyone
      // else is out of role. Removing it while the item is in Review changes
      // nothing about the position.
      const current = state.reviewRequired ?? false;
      if (command.required === current) {
        return errOf('no_change', m('itemErrors.contract.reviewRequiredUnchanged'));
      }
      const level = isRequester(actor, state)
        ? undefined
        : isContributor(actor, state) && !command.required
          ? 'critical'
          : 'warning';
      return single({
        action: 'field_changed',
        previousValue: { reviewRequired: current },
        newValue: { reviewRequired: command.required },
        level,
      });
    }

    case 'SetInheritsAccess': {
      if (command.inheritsAccess === state.inheritsAccess) {
        return errOf('no_change', m('itemErrors.contract.inheritanceUnchanged'));
      }
      return single({
        action: 'field_changed',
        previousValue: { inheritsAccess: state.inheritsAccess },
        newValue: { inheritsAccess: command.inheritsAccess },
        level: flag(actor, state, 'requester'),
      });
    }

    case 'Reassign': {
      if (command.role === 'requester') {
        // The requester seat: hand off, add, remove a co-owner, or walk away
        // entirely. Whether the actor is *allowed* to touch it is the server's
        // reserved-act gate, not here. Emptying the seat is LEGAL and loud
        // (docs/HEALTH_SIGNALS.md §4, amending CONTRACT_SPINE §2): the last
        // requester leaving breaks the accountability chain - the two loudest
        // facts ("nobody wants this" / "nobody will make sure this happens")
        // hang off exactly this event, so it carries `critical`, one level
        // louder than the contributor's self-walk-away warning (an open doer
        // seat is honest need; a broken ownership chain pages). No new event
        // type: the seat event IS the vacancy trigger (HS-C1). Birth still
        // always seats the creator (decideCreate) - vacancy only ever arises
        // from this explicit, logged act.
        const next = dedupeRefs(command.to);
        if (sameRefSet(next, state.requesters)) {
          return errOf('no_change', m('itemErrors.contract.requestersUnchanged'));
        }
        return single({
          action: 'reassigned',
          previousValue: { requesters: state.requesters },
          newValue: { requesters: next },
          level: next.length === 0 ? 'critical' : flag(actor, state, 'requester'),
        });
      }
      // role === 'contributor': set the doer membership (add, remove, hand off, or
      // open with `[]`). Changing the set does **not** touch phase or verdict (§6).
      const next = dedupeRefs(command.to ?? []);
      if (sameRefSet(next, state.contributors)) {
        return errOf(
          'no_change',
          next.length === 0
            ? m('itemErrors.contract.alreadyOpen')
            : m('itemErrors.contract.contributorsUnchanged'),
        );
      }
      // Abandoning the last doer is flagged (§2, decided 2026-07-06): a
      // SELF-removal that leaves the contributor seat EMPTY carries `warning` -
      // an open seat is honest need and stays quiet, but a silent walk-away
      // that leaves the work doerless is attention-worthy. Stepping down while
      // co-contributors remain is ordinary re-arrangement (unflagged); someone
      // ELSE emptying the seat stays governed by the ordinary party flag.
      const selfWalkAway =
        next.length === 0 &&
        isContributor(actor, state) &&
        !next.some((r) => refsEqual(r, actor.ref));
      return single({
        action: 'reassigned',
        previousValue: { contributors: state.contributors },
        newValue: { contributors: next },
        level: selfWalkAway ? 'warning' : flag(actor, state, 'party'),
      });
    }

    // The user tag system (docs/FIELDS_AND_TAGS.md §6.2): a tag is the string,
    // so applying and removing are ITEM acts, open to anyone who may act on
    // the item (no out-of-role flag: filing is not a contract move). Worn is
    // by FOLD: `Area` and `area` are one tag, and the row keeps the first
    // spelling. Shape B: the projection is an `item_tags` row, not a column.
    case 'Tag': {
      const parsed = parseTagPath(command.tag);
      if (!parsed.ok) return parsed;
      const { tag, fold } = parsed.value;
      if ((state.tagFolds ?? []).includes(fold)) {
        return errOf('no_change', m('itemErrors.contract.alreadyTagged', { tag }));
      }
      return single({ action: 'item_tagged', previousValue: null, newValue: { tag, fold } });
    }

    case 'Untag': {
      const parsed = parseTagPath(command.tag);
      if (!parsed.ok) return parsed;
      const { tag, fold } = parsed.value;
      if (!(state.tagFolds ?? []).includes(fold)) {
        return errOf('no_change', m('resolution.refusal.notTagged', { tag }));
      }
      return single({ action: 'item_untagged', previousValue: { tag, fold }, newValue: null });
    }

    case 'Comment': {
      if (command.text.trim().length === 0) {
        return errOf('invalid_input', m('itemErrors.contract.commentEmpty'));
      }
      return single({
        action: 'commented',
        previousValue: null,
        newValue: null,
        comment: command.text,
      });
    }

    case 'Acknowledge': {
      return single({ action: 'acknowledged', previousValue: null, newValue: null });
    }
  }
}

/**
 * The state an event leaves (the fold over one decided event): a column delta
 * merges into the state; a user tag event moves the worn set instead, since a
 * tag is a row, not a column (shape B). The resolver's plan fold uses this so
 * a plan's later steps decide against what its earlier ones did.
 */
export function stateAfter(state: WorkItemState, event: ProposedEvent): WorkItemState {
  if (event.action === 'item_tagged' || event.action === 'item_untagged') {
    const value = (event.newValue ?? event.previousValue) as ItemTagValue;
    const worn = (state.tagFolds ?? []).filter((f) => f !== value.fold);
    return {
      ...state,
      tagFolds: event.action === 'item_tagged' ? [...worn, value.fold] : worn,
    };
  }
  return event.newValue ? { ...state, ...(event.newValue as Partial<WorkItemState>) } : state;
}

/**
 * Validate the creation of a work item. Only a title is required. The requester
 * seat defaults to the creator (birth is never vacant, §2; the seat may empty
 * later only via an explicit Reassign walk-away - docs/HEALTH_SIGNALS.md §4);
 * the contributor seat defaults to empty (open, "needs doing"). The atoms are
 * born all-false, verdict `open`. Returns the initial state and the `created`
 * event.
 */
export function decideCreate(
  input: CreateWorkItemInput,
  actor: string,
): Result<{ initial: WorkItemState; event: ProposedEvent }> {
  if (input.title.trim().length === 0) {
    return errOf('invalid_input', m('itemErrors.contract.workItemTitleNeeded'));
  }

  const requesters = dedupeRefs(input.requesters ?? [actor]);
  const owners = requesters.length > 0 ? requesters : [actor];

  // Birth atoms (docs/CLOSED_AXIS.md): ALWAYS all-false. Birth is a plain
  // create (docs/archive/AUTOMATION_AUDIT.md, ruling 2 - 2026-07-22): creating INTO a
  // flow stage lane no longer folds the stage's atoms here; positioning is a
  // regular add-to that applies the stage's full `means` as real acts AFTER
  // the create (createWorkItemTx). There is no phase to seed - an item in no
  // flow has no phase at all.
  const standings: Standings = {
    delivered: false,
    active: false,
    closed: false,
  };

  const initial: WorkItemState = {
    ...standings,
    verdict: 'open',
    requesters: owners,
    contributors: dedupeRefs(input.contributors ?? []),
    priority: input.priority ?? null,
    acceptanceCriteria: input.acceptanceCriteria ?? null,
    description: input.description ?? null,
    // The notice is authored after birth (SetNote), never seeded at creation.
    note: null,
    // Private by default (§3 / MODEL §6) in BOTH org kinds: the commons is born
    // protected by decision (docs/COMMONS.md) and the jurisdiction default was
    // already protected. One pure definition, so the default cannot fork.
    visibility: input.visibility ?? 'protected',
    inheritsAccess: input.inheritsAccess ?? true,
    title: input.title,
  };

  return ok({
    initial,
    event: { action: 'created', previousValue: null, newValue: { ...initial } },
  });
}

// --- A create's idempotency key (docs/LOADING.md §3.4, docs/SYNC.md §7.4) -----

/** The longest idempotency key a create accepts. A device mints far shorter
 *  ones (`mintKey`, src/components/live-queue.ts); the bound keeps a client
 *  from parking arbitrary text in the log's indexed column. */
export const COMMAND_KEY_MAX = 128;

/** Whether a value is a well-formed idempotency key: a non-empty string of at
 *  most `COMMAND_KEY_MAX` word characters, dots, colons and dashes. One rule
 *  for every key a client sends: a create's (`decideCreateReceipt`) and a
 *  replayed entry's (`replayCommands`, src/server/replay.ts). Pure. */
export function isWellFormedKey(key: unknown): key is string {
  return (
    typeof key === 'string' &&
    key.length > 0 &&
    key.length <= COMMAND_KEY_MAX &&
    /^[\w.:-]+$/.test(key)
  );
}

/** What the log already holds under a key, as the spine gathered it: the one
 *  event that carries `(principal, command_key)`, or null. */
export interface KeyedEvent {
  subjectType: string;
  subjectId: string;
  action: string;
}

/**
 * Whether a create with this idempotency key is a NEW birth or a second arrival
 * of one the log already holds (the retry after a lost reply). The key rides the
 * `created` event of the item it made, so a prior event under the same key that
 * is anything else is a key reused for a different act: refused, never answered
 * with an unrelated subject. Pure: the spine looks the key up in the create's
 * own transaction and passes what it found.
 */
export function decideCreateReceipt(
  key: string,
  prior: KeyedEvent | null,
): Result<{ kind: 'create' } | { kind: 'receipt'; itemId: string }> {
  if (!isWellFormedKey(key)) {
    return errOf('invalid_input', m('itemErrors.contract.createKeyMalformed'));
  }
  if (prior === null) return ok({ kind: 'create' });
  if (prior.subjectType !== 'work_item' || prior.action !== 'created') {
    return errOf('invalid_input', m('itemErrors.contract.createKeyReused'));
  }
  return ok({ kind: 'receipt', itemId: prior.subjectId });
}

// --- Push rules (§2, D3): the landing of a seat set by another party ---------
export interface PushRule {
  mode: PushMode;
  /** For `circle`, the explicit circle of refs that may seat the subject to
   *  `held` directly. Ignored for `ask_all`. */
  circleRefs: string[];
}

/**
 * Where a seat set by `offeredBy` lands for a subject whose push rule is `rule`
 * (§2). Pure: the server gathers the membership facts (`coMemberRefs`, the
 * subject's default circle when no row exists) and passes them in.
 *
 *   - `rule === null` (no push_rules row): the **default circle** = the subject's
 *     co-members (`coMemberRefs`). A co-member seats to `held`; anyone else offers.
 *   - `rule.mode === 'circle'`: the explicit `circleRefs` are the circle.
 *   - `rule.mode === 'ask_all'`: "protected", an empty circle read backwards -
 *     everyone must ask, so every push lands `offered`.
 *
 * Self-claim (a subject seating themselves) is not routed here: it is the `Claim`
 * path and always lands `held` (§2).
 */
export function resolvePushLanding(
  rule: PushRule | null,
  offeredBy: string,
  coMemberRefs: readonly string[],
): SeatState {
  const circle = rule === null ? coMemberRefs : rule.mode === 'ask_all' ? [] : rule.circleRefs;
  return circle.some((r) => refsEqual(r, offeredBy)) ? 'held' : 'offered';
}
